import type { WsEvent } from '../../../shared/types';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class Api {
  constructor(public baseUrl: string, public token: string | null) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, 'Server non raggiungibile');
    }
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!res.ok) {
      const details = Array.isArray(data?.details) ? `: ${data.details.join('; ')}` : '';
      throw new ApiError(res.status, (data?.error ?? `Errore ${res.status}`) + details);
    }
    return data as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body ?? {});
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body ?? {});
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body ?? {});
  }
  del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /** Scarica un file (allegati): richiesta autenticata che restituisce il contenuto. */
  async download(path: string): Promise<Blob> {
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, { headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    } catch {
      throw new ApiError(0, 'Server non raggiungibile');
    }
    if (!res.ok) throw new ApiError(res.status, `Errore ${res.status}`);
    return res.blob();
  }

  wsUrl(): string {
    const u = new URL(this.baseUrl);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.pathname = '/ws';
    u.search = `?token=${encodeURIComponent(this.token ?? '')}`;
    return u.toString();
  }
}

/** Connessione WebSocket con riconnessione automatica. Restituisce la funzione per chiudere. */
export function connectWs(api: Api, onEvent: (ev: WsEvent) => void, onState: (open: boolean) => void): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry = 1000;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    if (closed) return;
    try {
      ws = new WebSocket(api.wsUrl());
    } catch {
      schedule();
      return;
    }
    ws.onopen = () => {
      retry = 1000;
      onState(true);
    };
    ws.onmessage = (m) => {
      try {
        onEvent(JSON.parse(String(m.data)) as WsEvent);
      } catch {
        // messaggio non valido: ignorato
      }
    };
    ws.onclose = () => {
      onState(false);
      schedule();
    };
    ws.onerror = () => {
      ws?.close();
    };
  };

  const schedule = () => {
    if (closed) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(open, retry);
    retry = Math.min(retry * 2, 30_000);
  };

  open();
  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    ws?.close();
  };
}
